import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Typography, Card, Table, Button, Modal, Form, Input, Switch, Space, message, Tooltip } from 'antd';
import { WarningOutlined, TableOutlined } from '@ant-design/icons';
import { tenantService } from '../services/tenants';
import MainLayout from '../components/MainLayout';
import ItemDetailDrawer from '../components/ItemDetailDrawer';
import { documentService } from '../services/documents';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { downloadXlsx } from '../utils/excelExport';
import './Dashboard.css';

const { Title } = Typography;

function Companies() {
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [missingDocsByItemId, setMissingDocsByItemId] = useState({});
  const [form] = Form.useForm();
  const { selectedTenantId } = useTenant();
  const { user } = useAuth();
  const [tableSort, setTableSort] = useState(null);
  
  // Check if user is tenant_user (external user)
  const isTenantUser = user?.role === 'tenant_user';
  const isAdmin = user?.role === 'admin';
  const canMutateCompanies = user?.role !== 'all_external';

  const validateFiscalCode = (_, value) => {
    if (!value) return Promise.resolve();
    const upper = value.toUpperCase();
    return /^[A-Z0-9]{16}$/.test(upper)
      ? Promise.resolve()
      : Promise.reject('CF deve essere 16 caratteri alfanumerici');
  };

  const validateVatNumber = (_, value) => {
    if (!value) return Promise.resolve();
    return /^[A-Za-z0-9]{1,20}$/.test(value)
      ? Promise.resolve()
      : Promise.reject('P.IVA accetta fino a 20 caratteri alfanumerici');
  };

  const formatError = (err) => {
    const detail = err?.response?.data?.detail;
    if (!detail) return 'Errore';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map((d) => d.msg || d.detail || JSON.stringify(d)).join(' | ');
    }
    if (typeof detail === 'object') return detail.msg || detail.detail || JSON.stringify(detail);
    return 'Errore';
  };

  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedTenantId) params.tenant_id = selectedTenantId;
      const res = await tenantService.list(params);
      setData(res || []);
      checkMissingDocs(res || []);
    } catch (e) {
      message.error('Errore nel caricamento aziende');
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId]);

  useEffect(() => {
    loadTenants();
  }, [selectedTenantId, loadTenants]);

  const filteredData = useMemo(() => {
    if (!search) return data;
    const term = search.toLowerCase();
    return data.filter((item) => Object.values(item || {}).some((val) => (val !== null && val !== undefined) && String(val).toLowerCase().includes(term)));
  }, [data, search]);

  const stringSorter = (key) => (a, b) => (a[key] || '').localeCompare(b[key] || '');
  const boolSorter = (key) => (a, b) => Number(a[key]) - Number(b[key]);

  const sortComparators = {
    name: stringSorter('name'),
    code: stringSorter('code'),
    fiscal_code: stringSorter('fiscal_code'),
    vat_number: stringSorter('vat_number'),
    contact_person: stringSorter('contact_person'),
    email: stringSorter('email'),
    pec_email: stringSorter('pec_email'),
    phone: stringSorter('phone'),
    is_active: boolSorter('is_active'),
  };

  const sortedData = useMemo(() => {
    if (!tableSort?.order || !tableSort?.field || !sortComparators[tableSort.field]) {
      return filteredData;
    }
    const arr = [...filteredData];
    arr.sort(sortComparators[tableSort.field]);
    if (tableSort.order === 'descend') arr.reverse();
    return arr;
  }, [filteredData, tableSort]);

  const handleTableChange = (_, __, sorter) => {
    if (sorter && sorter.order) {
      setTableSort({ field: sorter.field || sorter.columnKey, order: sorter.order });
    } else {
      setTableSort(null);
    }
  };

  const handleExportXlsx = () => {
    const headers = ['ID', 'Nome', 'Codice', 'CF', 'P.IVA', 'Referente', 'Email', 'PEC', 'Telefono', 'Attivo'];
    const rows = (sortedData || []).map((item) => [
      item.id,
      item.name || '',
      item.code || '',
      item.fiscal_code || '',
      item.vat_number || '',
      item.contact_person || '',
      item.email || '',
      item.pec_email || '',
      item.phone || '',
      item.is_active ? 'Sì' : 'No',
    ]);
    downloadXlsx('aziende.xlsx', headers, rows);
  };

  const onSubmit = async (values) => {
    try {
      const payload = { ...values };
      if (payload.fiscal_code) payload.fiscal_code = payload.fiscal_code.toUpperCase().trim();
      if (payload.vat_number) payload.vat_number = payload.vat_number.trim();
      if (payload.phone) payload.phone = payload.phone.trim();
      if (!payload.code || payload.code.trim() === '') {
        const generated = `AZI-${Date.now().toString(36).toUpperCase()}`;
        payload.code = generated;
      }
      
      // If editing as tenant_user, remove restricted fields from payload
      if (editing && isTenantUser) {
        const restrictedFields = ['email', 'fiscal_code', 'vat_number', 'is_active', 'is_nke', 'tax_id', 'code'];
        restrictedFields.forEach(field => delete payload[field]);
      }
      
      if (editing) {
        await tenantService.update(editing.id, payload);
        message.success('Azienda aggiornata');
      } else {
        await tenantService.create(payload);
        message.success('Azienda creata');
      }
      setModalOpen(false);
      setEditing(null);
      form.resetFields();
      loadTenants();
    } catch (e) {
      message.error(formatError(e));
    }
  };

  const handleEdit = (record) => {
    setEditing(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleDisable = async (record) => {
    try {
      const toActive = !record.is_active;
      await tenantService.update(record.id, { is_active: toActive });
      message.success(toActive ? 'Azienda attivata' : 'Azienda disattivata');
      loadTenants();
    } catch (e) {
      message.error(formatError(e));
    }
  };

  const checkMissingDocs = async (items) => {
    const missingMap = {};
    const requiredDocs = ['Visura camerale', 'DURC', 'DURF', 'Polizza RCT/RCO', 'Documento di Valutazione dei Rischi', 'Nomina RSPP', 'Formazione RSPP', 'Elezione RLS', 'Nomina RLS', 'Formazione RLS', 'Nomina Medico Competente', 'ALI 5', 'ALI 7'];
    
    for (const item of items) {
      try {
        const targetItemId = item.company_item_id || item.id;
        const docs = await documentService.list({ item_id: targetItemId });
        const loadedDocTypes = docs.map(d => d.document_type);
        const missing = requiredDocs.filter(reqDoc => !loadedDocTypes.includes(reqDoc));
        if (missing.length > 0) {
          missingMap[item.id] = missing;
        }
      } catch (e) {
        console.error(`Errore caricamento documenti per azienda ${item.id}:`, e);
      }
    }
    setMissingDocsByItemId(missingMap);
  };

  const columns = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      sorter: stringSorter('name'),
      sortDirections: ['ascend', 'descend'],
      sortOrder: tableSort?.field === 'name' ? tableSort.order : null,
      render: (text, record) => (
        <Space>
          {missingDocsByItemId[record.id] && (
            <Tooltip title={`Documenti mancanti: ${missingDocsByItemId[record.id].join(', ')}`}>
              <WarningOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />
            </Tooltip>
          )}
          <span>{text}</span>
        </Space>
      ),
    },
    { title: 'Codice', dataIndex: 'code', key: 'code', sorter: stringSorter('code'), sortDirections: ['ascend', 'descend'], sortOrder: tableSort?.field === 'code' ? tableSort.order : null },
    { title: 'CF', dataIndex: 'fiscal_code', key: 'fiscal_code', sorter: stringSorter('fiscal_code'), sortDirections: ['ascend', 'descend'], sortOrder: tableSort?.field === 'fiscal_code' ? tableSort.order : null },
    { title: 'P.IVA', dataIndex: 'vat_number', key: 'vat_number', sorter: stringSorter('vat_number'), sortDirections: ['ascend', 'descend'], sortOrder: tableSort?.field === 'vat_number' ? tableSort.order : null },
    { title: 'Referente', dataIndex: 'contact_person', key: 'contact_person', sorter: stringSorter('contact_person'), sortDirections: ['ascend', 'descend'], sortOrder: tableSort?.field === 'contact_person' ? tableSort.order : null },
    { title: 'Email', dataIndex: 'email', key: 'email', sorter: stringSorter('email'), sortDirections: ['ascend', 'descend'], sortOrder: tableSort?.field === 'email' ? tableSort.order : null },
    { title: 'PEC', dataIndex: 'pec_email', key: 'pec_email', sorter: stringSorter('pec_email'), sortDirections: ['ascend', 'descend'], sortOrder: tableSort?.field === 'pec_email' ? tableSort.order : null },
    { title: 'Telefono', dataIndex: 'phone', key: 'phone', sorter: stringSorter('phone'), sortDirections: ['ascend', 'descend'], sortOrder: tableSort?.field === 'phone' ? tableSort.order : null },
    { title: 'Attivo', dataIndex: 'is_active', key: 'is_active', render: (v) => (v ? 'Sì' : 'No'), sorter: boolSorter('is_active'), sortDirections: ['ascend', 'descend'], sortOrder: tableSort?.field === 'is_active' ? tableSort.order : null },
    ...(canMutateCompanies ? [{
      title: 'Azioni',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={(e) => { e.stopPropagation(); handleEdit(record); }}>Modifica</Button>
          {!isTenantUser && (
            <Button size="small" danger={record.is_active} onClick={(e) => { e.stopPropagation(); handleDisable(record); }}>
              {record.is_active ? 'Disattiva' : 'Attiva'}
            </Button>
          )}
        </Space>
      ),
    }] : []),
  ];

  const handleRowClick = (record) => {
    const companyItemId = record.company_item_id || record.id;
    setSelectedItem({ ...record, id: companyItemId });
    setDrawerOpen(true);
  };

  const handleBulkDisable = async () => {
    try {
      await Promise.all(selectedRowKeys.map(id => tenantService.update(id, { is_active: false })));
      message.success('Aziende disattivate con successo');
      setSelectedRowKeys([]);
      loadTenants();
    } catch (e) {
      message.error(formatError(e));
    }
  };

  const handleBulkDelete = () => {
    if (!selectedRowKeys.length) return;
    Modal.confirm({
      title: 'Confermi la rimozione bulk delle aziende selezionate?',
      okType: 'danger',
      okText: 'Rimuovi',
      cancelText: 'Annulla',
      onOk: async () => {
        try {
          await tenantService.bulkCascadeDelete(selectedRowKeys);
          message.success('Aziende rimosse con successo');
          setSelectedRowKeys([]);
          loadTenants();
        } catch (e) {
          message.error(formatError(e));
        }
      },
    });
  };

  const rowSelection = isAdmin ? {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  } : null;

  return (
    <MainLayout title="Aziende">
      <Card style={{ marginBottom: 24 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <Title level={2} style={{ margin: 0 }}>Aziende</Title>
          <Button icon={<TableOutlined />} onClick={handleExportXlsx}>Esporta Excel</Button>
          <Input.Search
            allowClear
            placeholder="Cerca in tutti i campi"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 320 }}
          />
          <Space>
            {isAdmin && selectedRowKeys.length > 0 && (
              <Button danger type="primary" onClick={handleBulkDelete}>
                Elimina selezionati ({selectedRowKeys.length})
              </Button>
            )}
            {canMutateCompanies && (
              <Button type="primary" size="large" onClick={() => setModalOpen(true)}>+ Nuova Azienda</Button>
            )}
          </Space>
        </Space>
      </Card>

      <Card style={{ marginBottom: 24 }}>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={sortedData}
          columns={columns}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50"],
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
            showTotal: (total, range) => `${range[0]}-${range[1]} di ${total} aziende`,
          }}
          rowSelection={rowSelection}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

        <Modal
          title={editing ? 'Modifica Azienda' : 'Nuova Azienda'}
          open={modalOpen}
          onCancel={() => { setModalOpen(false); setEditing(null); form.resetFields(); }}
          onOk={() => form.submit()}
          okText={editing ? 'Salva' : 'Crea'}
          cancelText="Annulla"
          width={900}
        >
        <Form
          form={form}
          layout="vertical"
            onFinish={onSubmit}
          initialValues={{ is_active: true }}
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}
        >
          <Form.Item name="name" label="Nome" rules={[{ required: true, message: 'Inserisci il nome' }]} style={{ gridColumn: '1 / -1' }}> 
            <Input placeholder="Inserisci nome azienda" />
          </Form.Item>
          <Form.Item name="code" label="Codice azienda">
            <Input placeholder="Lascia vuoto per generare automaticamente" disabled={isTenantUser && editing} />
          </Form.Item>
          <Form.Item name="fiscal_code" label="Codice Fiscale" rules={[{ validator: validateFiscalCode }]}> 
            <Input placeholder="16 caratteri" maxLength={16} disabled={isTenantUser && editing} />
          </Form.Item>
          <Form.Item name="vat_number" label="Partita IVA" rules={[{ validator: validateVatNumber }]}> 
            <Input placeholder="Fino a 20 caratteri alfanumerici" maxLength={20} disabled={isTenantUser && editing} />
          </Form.Item>
          <Form.Item name="contact_person" label="Referente"> 
            <Input placeholder="Nome referente" />
          </Form.Item>
          <Form.Item name="employer" label="Datore di lavoro"> 
            <Input placeholder="Datore di lavoro" />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Email non valida' }]}> 
            <Input placeholder="email@example.it" disabled={isTenantUser && editing} />
          </Form.Item>
          <Form.Item name="pec_email" label="PEC" rules={[{ type: 'email', message: 'PEC non valida' }]}> 
            <Input placeholder="pec@pec.it" />
          </Form.Item>
          <Form.Item name="address" label="Sede" style={{ gridColumn: '1 / -1' }}> 
            <Input placeholder="Indirizzo sede" />
          </Form.Item>
          <Form.Item name="phone" label="Telefono" rules={[{ pattern: /^[0-9+().\s-]{6,20}$/, message: 'Inserisci un telefono valido' }]}> 
            <Input placeholder="Telefono" />
          </Form.Item>
          <Form.Item name="rls" label="RLS"> 
            <Input placeholder="Nome RLS" />
          </Form.Item>
          <Form.Item name="medico_competente" label="Medico competente"> 
            <Input placeholder="Nome medico competente" />
          </Form.Item>
          <Form.Item name="rspp" label="RSPP"> 
            <Input placeholder="Nome RSPP" />
          </Form.Item>
          <Form.Item name="is_active" label="Attivo" valuePropName="checked">
            <Switch disabled={isTenantUser && editing} />
          </Form.Item>
        </Form>
      </Modal>

      <ItemDetailDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        item={selectedItem}
        itemType="company"
      />
    </MainLayout>
  );
}

export default Companies;
